import type { Ton } from '../commun.js'

/**
 * RÉSEAU — LE MODÈLE (fonctions pures, 21/10/2026).
 *
 * Formes de Sonde (contrat v1) réduites à ce que l'écran Réseau lit, et les
 * règles d'affichage qui ne doivent jamais dériver :
 *   · un TPE à présence INCONNUE (`present = null`) n'est jamais peint absent ;
 *   · un port éteint sans niveau est « libre », pas une panne ;
 *   · une mesure trop vieille est grise (périmée), jamais verte ;
 *   · « — » pour une valeur absente, jamais 0 ;
 *   · aucun texte de conseil ne reprend une suggestion refusée par Hugo (§14).
 */

/* ═══════════════ Formes communes de Sonde ═══════════════ */

export type NiveauMesure = 'ok' | 'warning' | 'critical' | null

export interface Collecteur { ok: boolean; stale: boolean; heartbeat_age_s: number | null; code: string | null; detail_fr: string | null }

export interface SourceDoc {
  code: string
  label: string
  status: string
  status_label: string
  since: string | null
  last_success_at: string | null
  last_error_label: string | null
  consecutive_failures: number
}

export interface Seuil { warn: number | null; crit: number | null; direction: 'above' | 'below'; unit: string | null }

export interface EtatAppareil {
  display_state: string
  display_label: string
  greyed: boolean
  since: string | null
  data_age_s: number | null
  cause_label: string | null
  flapping: boolean
  exception: { kind: string; reason: string; until: string | null } | null
}

export interface ResumeAppareil {
  uid: string
  display_name: string
  kind: string
  family: string
  family_label: string
  zone_code: string | null
  criticality: string
  state: EtatAppareil
}

export interface DocDomaine {
  generated_at: string
  collector: Collecteur
  sources: SourceDoc[]
  thresholds?: Record<string, Seuil>
}

/** Une sonde active de Sonde (ping, DNS, HTTPS, TCP, URL d'un service). */
export interface SondeMesure { probe_key: string; ok: boolean | null; rtt_ms: number | null; loss_pct: number | null; latency_ms: number | null; http_status: number | null; err_class: string | null; level: NiveauMesure; at: string | null; data_age_s: number | null }

export interface MesureGenerique { entity_key: string; metric: string; label: string; unit: string | null; value: number | null; uid: string | null; at: string; data_age_s: number }

/* ═══════════════ Vues réseau ═══════════════ */

export interface DocWan extends DocDomaine {
  gateway: { device: ResumeAppareil; rx_bps: number | null; tx_bps: number | null; cpu_pct: number | null; mem_pct: number | null; at: string | null; data_age_s: number | null } | null
  probes: SondeMesure[]
  generic: MesureGenerique[]
}

export interface Port {
  port_idx: number
  up: boolean | null
  speed_mbps: number | null
  full_duplex: boolean | null
  rx_bps: number | null
  tx_bps: number | null
  util_pct: number | null
  rx_err_5m: number | null
  tx_err_5m: number | null
  rx_drop_5m: number | null
  tx_drop_5m: number | null
  link_down_24h: number | null
  poe_w: number | null
  poe_on: boolean | null
  mac_count: number | null
  peer: { uid: string; display_name: string; display_state: string; link_kind: string } | null
  level: NiveauMesure
  at: string | null
  data_age_s: number | null
}

export interface Switch {
  device: ResumeAppareil
  poe_budget_w: number | null
  poe_used_w: number | null
  poe_pct: number | null
  poe_level: NiveauMesure
  ports: Port[]
}

export interface DocPorts extends DocDomaine { switches: Switch[] }

export interface Radio {
  radio: string
  band: '2.4' | '5' | '6' | null
  channel: number | null
  width_mhz: number | null
  cu_total_pct: number | null
  cu_interf_pct: number | null
  clients: number | null
  tx_retries_pct: number | null
  tx_power_dbm: number | null
  satisfaction: number | null
  level: NiveauMesure
  at: string | null
  data_age_s: number | null
}

export interface Borne { device: ResumeAppareil; radios: Radio[]; tracked_clients: string[] }

export interface RisqueClient { score: number | null; level: 'low' | 'medium' | 'high' | null; main_factor: string | null; explanation_fr: string | null }

export interface ClientSuivi {
  device: ResumeAppareil
  ap_uid: string | null
  ap_name: string | null
  wired: boolean | null
  channel: number | null
  rssi_dbm: number | null
  satisfaction: number | null
  tx_retries_pct: number | null
  reconnects_15m: number | null
  roams_15m: number | null
  risk: RisqueClient
  at: string | null
  data_age_s: number | null
}

export interface MeteoCanal24h { hour: string; band: '2.4' | '5' | '6'; channel: number; cu_total_avg: number | null; cu_interf_avg: number | null; radios: number }

export interface DocWifi extends DocDomaine {
  access_points: Borne[]
  tracked_clients: ClientSuivi[]
  channel_weather_24h: MeteoCanal24h[]
  remarks: { ap_uid: string; text_fr: string }[]
}

export interface DocClients extends DocDomaine {
  clients: ClientSuivi[]
  untracked_by_vlan: Record<string, number> | null
  untracked_at?: string | null
  untracked_age_s?: number | null
}

export interface NoeudTopo { uid: string; display_name: string; kind: string; family: string; zone_code: string | null; display_state: string; greyed: boolean }
export interface LienTopo { kind: string; from_uid: string; to_uid: string; port: number | string | null; origin: 'auto' | 'declared'; confidence: number }
export interface DocTopologie { generated_at: string; mode: 'live' | 'replay'; nodes: NoeudTopo[]; links: LienTopo[] }

export interface Terminal {
  uid: string
  name: string
  zone: string | null
  present: boolean | null
  rssi_dbm: number | null
  tx_retries_pct: number | null
  satisfaction: number | null
  channel: number | null
  risk: number | null
  level: 'faible' | 'moyen' | 'élevé' | null
  seen_at: string | null
}

export interface BornePaiement {
  uid: string
  name: string
  zone: string | null
  band: string | null
  channel: number | null
  cu_total_pct: number | null
  clients: number | null
  terminals: Terminal[]
  worst_risk: number | null
  constat_fr: string | null
  advice_fr: string | null
}

export interface DocRisquePaiement {
  computed_at: string
  age_s: number | null
  live: boolean
  summary_fr: string
  source: { code: string; status: string | null; available: boolean; last_success_at: string | null }
  aps: BornePaiement[]
  absent: Terminal[]
  unknown: Terminal[]
}

export interface DocMeteoWifi {
  computed_at: string
  age_s: number | null
  live: boolean
  band: 'ng' | 'na'
  days: number
  cells: { channel: number; hour: number; cu_avg: number | null; cu_max: number | null; samples: number }[]
  channels: { channel: number; cu_avg: number | null; cu_peak: number | null; neighbors: number | null; neighbors_max_signal_dbm: number | null; aps: number | null }[]
  aps: { uid: string; name: string; zone: string | null; channel: number | null; cu_avg: number | null; cu_peak: number | null; clients_avg: number | null; busy_without_clients: boolean }[]
  findings_fr: string[]
}

export interface DocDesaccords {
  computed_at: string
  age_s: number | null
  items: {
    key: string
    kind: 'witness' | 'ghost' | 'parc_state'
    uid: string | null
    name: string
    message_fr: string
    sides: { source: string; witness: string; says: 'present' | 'absent' | 'other'; observed_at: string | null; detail?: string | null }[]
    observed_at: string
  }[]
}

/* ═══════════════ Gardes de forme (minimales : la page affine) ═══════════════ */

const objet = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null && !Array.isArray(x)
const liste = (x: Record<string, unknown>, k: string) => Array.isArray(x[k])
const domaine = (x: unknown): x is Record<string, unknown> => objet(x) && objet(x['collector']) && liste(x, 'sources')

export const estDocWan = (x: unknown): x is DocWan => domaine(x) && liste(x, 'probes') && (x['gateway'] === null || objet(x['gateway']))
export const estDocPorts = (x: unknown): x is DocPorts => domaine(x) && liste(x, 'switches')
export const estDocWifi = (x: unknown): x is DocWifi => domaine(x) && liste(x, 'access_points') && liste(x, 'channel_weather_24h') && liste(x, 'remarks')
export const estDocClients = (x: unknown): x is DocClients => domaine(x) && liste(x, 'clients') && (x['untracked_by_vlan'] === null || objet(x['untracked_by_vlan']))
export const estDocTopologie = (x: unknown): x is DocTopologie => objet(x) && liste(x, 'nodes') && liste(x, 'links')
export const estDocRisquePaiement = (x: unknown): x is DocRisquePaiement =>
  objet(x) && typeof x['summary_fr'] === 'string' && objet(x['source']) && liste(x, 'aps') && liste(x, 'absent') && liste(x, 'unknown')
export const estDocMeteoWifi = (x: unknown): x is DocMeteoWifi => objet(x) && liste(x, 'cells') && liste(x, 'channels') && liste(x, 'findings_fr')
export const estDocDesaccords = (x: unknown): x is DocDesaccords => objet(x) && liste(x, 'items')

/* ═══════════════ Couleurs : UN code pour tout le site ═══════════════ */

/** Niveau d'une mesure face à son seuil (ok / warning / critical) ; null = pas de verdict = gris. */
export const tonMesure = (l: NiveauMesure | undefined): Ton => l === 'critical' ? 'err' : l === 'warning' ? 'warn' : l === 'ok' ? 'ok' : 'gris'
export const LIBELLE_MESURE: Record<'ok' | 'warning' | 'critical', string> = { ok: 'Normal', warning: 'À surveiller', critical: 'Critique' }

/** Statut d'une source de Sonde : indisponible = gris, jamais rouge. */
export const tonSource = (s: string): Ton => s === 'OK' ? 'ok' : s === 'DEGRADED' ? 'warn' : 'gris'

/** Âge au-delà duquel une mesure est dite périmée (grise), par défaut 15 min. */
export const PERIME_S = 900

export const estPerime = (age: number | null | undefined, limite = PERIME_S): boolean =>
  age === null || age === undefined || !Number.isFinite(age) || age > limite

/**
 * Niveau d'une valeur face à un seuil de Sonde — même règle que Sonde
 * (`measureLevel`) : `above` → ≥ crit critique, ≥ warn attention ; `below` à
 * l'inverse. Sans valeur ou sans seuil : null (gris), jamais « ok ».
 */
export function niveauSeuil(v: number | null | undefined, s: Seuil | null | undefined): NiveauMesure {
  if (v === null || v === undefined || !Number.isFinite(v) || !s) return null
  if (s.direction === 'above') {
    if (s.crit !== null && v >= s.crit) return 'critical'
    if (s.warn !== null && v >= s.warn) return 'warning'
  } else {
    if (s.crit !== null && v <= s.crit) return 'critical'
    if (s.warn !== null && v <= s.warn) return 'warning'
  }
  return 'ok'
}

export function pireNiveau(...ls: NiveauMesure[]): NiveauMesure {
  const rang = { ok: 0, warning: 1, critical: 2 } as const
  let out: NiveauMesure = null
  for (const l of ls) if (l !== null && (out === null || rang[l] > rang[out])) out = l
  return out
}

/* ═══════════════ Ports ═══════════════ */

export type TonPort = Ton | 'libre'

/**
 * État affiché d'un port. Un port sans lien et sans niveau est LIBRE (rien
 * de branché, ou appareil éteint) : ni panne, ni vert. Une mesure périmée
 * est grise quel que soit son dernier niveau.
 */
export function etatPort(p: Pick<Port, 'up' | 'level' | 'data_age_s'>): { ton: TonPort; libelle: string } {
  if (estPerime(p.data_age_s)) return { ton: 'gris', libelle: 'Donnée périmée' }
  if (p.level === 'critical') return { ton: 'err', libelle: 'En défaut' }
  if (p.level === 'warning') return { ton: 'warn', libelle: 'À surveiller' }
  if (p.up === true) return { ton: 'ok', libelle: 'Lien actif' }
  if (p.up === false) return { ton: 'libre', libelle: 'Libre (aucun lien)' }
  return { ton: 'gris', libelle: 'Inconnu' }
}

export interface BilanSwitch { actifs: number; libres: number; alertes: number; inconnus: number; total: number; poe: number }

export function bilanSwitch(s: Pick<Switch, 'ports'>): BilanSwitch {
  const b: BilanSwitch = { actifs: 0, libres: 0, alertes: 0, inconnus: 0, total: s.ports.length, poe: 0 }
  for (const p of s.ports) {
    const e = etatPort(p)
    if (e.ton === 'ok') b.actifs++
    else if (e.ton === 'libre') b.libres++
    else if (e.ton === 'warn' || e.ton === 'err') b.alertes++
    else b.inconnus++
    if (p.poe_on === true) b.poe++
  }
  return b
}

/* ═══════════════ Radios Wi-Fi ═══════════════ */

/**
 * Ce qui fait le niveau d'une radio, selon les seuils publiés par Sonde :
 * occupation, interférences, renvois, satisfaction. On nomme le ou les
 * facteurs au pire niveau (« renvois 35,2 % »), pas seulement l'occupation.
 */
export function causeRadio(r: Pick<Radio, 'cu_total_pct' | 'cu_interf_pct' | 'tx_retries_pct' | 'satisfaction'>, seuils: Record<string, Seuil> | undefined): string {
  const f: [NiveauMesure, string][] = [
    [niveauSeuil(r.cu_total_pct, seuils?.['thresholds.wifi.cu_total_pct']), `occupation ${fmtPct(r.cu_total_pct)}`],
    [niveauSeuil(r.cu_interf_pct, seuils?.['thresholds.wifi.cu_interf_pct']), `interférences ${fmtPct(r.cu_interf_pct)}`],
    [niveauSeuil(r.tx_retries_pct, seuils?.['thresholds.wifi.tx_retries_pct']), `renvois ${fmtPct(r.tx_retries_pct, 1)}`],
    [niveauSeuil(r.satisfaction, seuils?.['thresholds.wifi.satisfaction']), `satisfaction ${fmtVal(r.satisfaction, '/100')}`],
  ]
  const pire = pireNiveau(...f.map((x) => x[0]))
  const causes = pire === null || pire === 'ok' ? [] : f.filter((x) => x[0] === pire).map((x) => x[1])
  return causes.length > 0 ? causes.join(' · ') : `occupation ${fmtPct(r.cu_total_pct)}`
}

/* ═══════════════ TPE et clients ═══════════════ */

/**
 * Pastille d'un TPE de la carte borne → TPE. `present = null` (UniFi muet) :
 * « Présence inconnue », GRIS — jamais « absent ».
 */
export function etatTerminal(t: Pick<Terminal, 'present' | 'level'>): { ton: Ton; libelle: string } {
  if (t.present === null) return { ton: 'gris', libelle: 'Présence inconnue' }
  if (t.present === false) return { ton: 'warn', libelle: 'Pas vu sur le Wi-Fi' }
  if (t.level === 'élevé') return { ton: 'err', libelle: 'Risque élevé' }
  if (t.level === 'moyen') return { ton: 'warn', libelle: 'Risque moyen' }
  if (t.level === 'faible') return { ton: 'ok', libelle: 'Risque faible' }
  return { ton: 'gris', libelle: 'Risque inconnu' }
}

export function etatRisqueClient(r: RisqueClient | null | undefined): { ton: Ton; libelle: string } {
  if (!r || r.level === null) return { ton: 'gris', libelle: 'Risque inconnu' }
  if (r.level === 'high') return { ton: 'err', libelle: 'Risque élevé' }
  if (r.level === 'medium') return { ton: 'warn', libelle: 'Risque moyen' }
  return { ton: 'ok', libelle: 'Risque faible' }
}

/** Clients non suivis : on COMPTE par VLAN (vie privée), jamais de liste. Tri numérique. */
export function vlansNonSuivis(v: Record<string, number> | null | undefined): { vlan: string; n: number }[] | null {
  if (!v) return null
  return Object.entries(v)
    .filter(([, n]) => typeof n === 'number' && Number.isFinite(n))
    .map(([vlan, n]) => ({ vlan, n }))
    .sort((a, b) => (Number(a.vlan) - Number(b.vlan)) || a.vlan.localeCompare(b.vlan))
}

/* ═══════════════ Topologie : qui est relié à quoi ═══════════════ */

/** Lien montant de chaque appareil (`uplink` ou `wifi_ap`) : `from` = l'aval, `to` = l'amont. */
export function liensMontants(t: DocTopologie | null): Map<string, { uid: string; nom: string; port: number | string | null; declare: boolean }> {
  const out = new Map<string, { uid: string; nom: string; port: number | string | null; declare: boolean }>()
  if (!t) return out
  const noms = new Map(t.nodes.map((n) => [n.uid, n.display_name]))
  for (const l of t.links) {
    if (l.kind !== 'uplink' && l.kind !== 'wifi_ap') continue
    const prec = out.get(l.from_uid)
    if (prec && l.kind === 'wifi_ap') continue
    out.set(l.from_uid, { uid: l.to_uid, nom: noms.get(l.to_uid) ?? l.to_uid, port: l.port, declare: l.origin === 'declared' })
  }
  return out
}

/* ═══════════════ Météo 2,4 GHz ═══════════════ */

export interface Meteo { mode: 'semaine' | 'jour' | 'vide'; canaux: number[]; heures: number[]; valeur: (canal: number, heure: number) => number | null }

/**
 * Grille canal × heure de l'occupation 2,4 GHz. Priorité à la moyenne de
 * Sonde sur plusieurs jours (`intel/wifi-weather`, heure de Paris) ; à
 * défaut, les dernières 24 h relevées par la vue Wi-Fi.
 */
export function grilleMeteo(semaine: DocMeteoWifi | null, jour: MeteoCanal24h[] | null, tz = 'Europe/Paris'): Meteo {
  if (semaine && semaine.band === 'ng' && semaine.cells.length > 0) {
    const m = new Map(semaine.cells.map((c) => [`${c.channel}:${c.hour}`, c.samples > 0 ? c.cu_avg : null]))
    const canaux = [...new Set(semaine.cells.map((c) => c.channel))].sort((a, b) => a - b)
    return { mode: 'semaine', canaux, heures: Array.from({ length: 24 }, (_, i) => i), valeur: (c, h) => m.get(`${c}:${h}`) ?? null }
  }
  const pts = (jour ?? []).filter((x) => x.band === '2.4')
  if (pts.length === 0) return { mode: 'vide', canaux: [], heures: [], valeur: () => null }
  const heureDe = (iso: string) => Number(new Date(iso).toLocaleString('fr-FR', { hour: '2-digit', hour12: false, timeZone: tz }).slice(0, 2))
  const m = new Map<string, number | null>()
  for (const p of pts) m.set(`${p.channel}:${heureDe(p.hour)}`, p.cu_total_avg)
  const canaux = [...new Set(pts.map((p) => p.channel))].sort((a, b) => a - b)
  const heures = [...new Set(pts.map((p) => heureDe(p.hour)))].sort((a, b) => a - b)
  return { mode: 'jour', canaux, heures, valeur: (c, h) => m.get(`${c}:${h}`) ?? null }
}

/* ═══════════════ Conseils : jamais une suggestion refusée (§14) ═══════════════ */

/**
 * Suggestions refusées par Hugo, à ne JAMAIS afficher (ni écran, ni conseil,
 * ni aide). Sonde filtre déjà ses textes (`alerts.forbidden_suggestions`) ;
 * Gestion refiltre tout texte de conseil reçu : un seul faux pas suffit.
 */
const INTERDITS: RegExp[] = [
  /min\s*-?\s*rate/i,
  /802\.11\s*r\b/i,
  /\bfast\s+(bss\s+)?transition\b/i,
  /r2[\s_-]?plonge/i,
  /\bbpdu\b/i,
  /\bstp\b/i,
  /spanning[\s-]?tree/i,
  /leave\s+session/i,
  /mediamtx[^.;]*\btcp\b/i,
  /\btcp\b[^.;]*mediamtx/i,
  /renomm\w*[^.;]*\bv5\b/i,
  /\bv5\b[^.;]*renomm/i,
  /\bntfy\b/i,
  /hors\s+(du\s+club|site)/i,
  /\b(copie|sauvegarde|envoi|témoin)\w*\s+(dans\s+le\s+|en\s+|vers\s+le\s+)?cloud\b/i,
]

export const conseilAutorise = (texte: string | null | undefined): texte is string =>
  typeof texte === 'string' && texte.trim() !== '' && !INTERDITS.some((r) => r.test(texte))

/* ═══════════════ Mise en forme ═══════════════ */

const nb = (n: number, d = 0) => n.toLocaleString('fr-FR', { maximumFractionDigits: d, minimumFractionDigits: 0 })

export function fmtDebit(bps: number | null | undefined): string {
  if (bps === null || bps === undefined || !Number.isFinite(bps)) return '—'
  if (bps < 1_000) return `${nb(bps)} bit/s`
  if (bps < 1_000_000) return `${nb(bps / 1_000, 1)} kbit/s`
  if (bps < 1_000_000_000) return `${nb(bps / 1_000_000, 1)} Mbit/s`
  return `${nb(bps / 1_000_000_000, 2)} Gbit/s`
}

export function fmtVitesse(mbps: number | null | undefined): string {
  if (mbps === null || mbps === undefined || !Number.isFinite(mbps)) return '—'
  return mbps >= 1000 ? `${nb(mbps / 1000, 1)} Gbit/s` : `${nb(mbps)} Mbit/s`
}

export const fmtPct = (v: number | null | undefined, d = 0): string =>
  v === null || v === undefined || !Number.isFinite(v) ? '—' : `${nb(v, d)} %`

export const fmtVal = (v: number | null | undefined, unite: string, d = 0): string =>
  v === null || v === undefined || !Number.isFinite(v) ? '—' : `${nb(v, d)}${unite ? ` ${unite}` : ''}`

export const BANDE: Record<string, string> = { '2.4': '2,4 GHz', '5': '5 GHz', '6': '6 GHz' }

/** Libellé lisible d'une sonde Internet (`internet:ping:…`, `internet:doh:<résolveur>:<nom>`…). */
export function libelleSondeInternet(brute: string): { type: string; cible: string } {
  // Sans le détail technique, le relais remplace les adresses par un jeton « masqué-… ».
  const cle = brute.replace(/masqué-[0-9a-f]{8}/g, 'adresse masquée')
  const [, type = '', ...reste] = cle.split(':')
  if (type === 'ping') return { type: 'Ping', cible: reste.join(':') }
  if (type === 'https') return { type: 'Site web (HTTPS)', cible: reste.join(':') }
  if (type === 'tcp') return { type: 'Connexion TCP', cible: reste.join(':') }
  if (type === 'doh') return { type: 'DNS chiffré', cible: reste.length > 1 ? `${reste.slice(1).join(':')} via ${reste[0]}` : reste.join(':') }
  return { type: type || 'Sonde', cible: reste.join(':') || cle }
}

/* ═══════════════ Ce qui demande attention ═══════════════ */

export const cheminAppareil = (uid: string) => `/infrastructure/appareils/${encodeURIComponent(uid)}`

export interface PointAttention { ton: Ton; texte: string; href?: string }

/** Rouge d'abord, puis ambre, puis le reste ; ordre d'arrivée conservé à rang égal. */
export function trierAttention(points: PointAttention[]): PointAttention[] {
  const rang: Record<Ton, number> = { err: 0, warn: 1, maint: 2, gris: 3, off: 4, ok: 5 }
  return points.map((p, i) => ({ p, i })).sort((a, b) => (rang[a.p.ton] - rang[b.p.ton]) || (a.i - b.i)).map((x) => x.p)
}

/** Seuls ces états sont un problème constaté ; gris (inconnu, périmé) et « éteint comme prévu » n'en sont pas. */
const PROBLEME: Record<string, Ton> = { OFFLINE: 'err', DEGRADED: 'warn' }

export function attentionReseau(d: {
  wan: DocWan | null
  ports: DocPorts | null
  wifi: DocWifi | null
  paiement: DocRisquePaiement | null
  desaccords: DocDesaccords | null
}): PointAttention[] {
  const out: PointAttention[] = []
  const appareil = (r: ResumeAppareil, quoi: string) => {
    const ton = PROBLEME[r.state.display_state]
    if (ton) out.push({ ton, texte: `${quoi} ${r.display_name} : ${r.state.display_label.toLowerCase()}`, href: cheminAppareil(r.uid) })
  }
  if (d.wan?.gateway) appareil(d.wan.gateway.device, 'Passerelle')
  for (const p of d.wan?.probes ?? []) {
    if (estPerime(p.data_age_s) || (p.level !== 'warning' && p.level !== 'critical')) continue
    const l = libelleSondeInternet(p.probe_key)
    const lat = p.latency_ms ?? p.rtt_ms
    const parts = [lat !== null ? fmtVal(lat, 'ms') : null, p.loss_pct ? `pertes ${fmtPct(p.loss_pct, 1)}` : null].filter((x): x is string => x !== null)
    const mesure = p.ok === false ? 'sans réponse' : parts.length > 0 ? parts.join(' · ') : LIBELLE_MESURE[p.level].toLowerCase()
    out.push({ ton: tonMesure(p.level), texte: `Internet — ${l.type} ${l.cible} : ${mesure}` })
  }
  for (const s of d.ports?.switches ?? []) {
    appareil(s.device, 'Switch')
    if (s.poe_level === 'warning' || s.poe_level === 'critical') {
      out.push({ ton: tonMesure(s.poe_level), texte: `${s.device.display_name} : budget PoE à ${fmtPct(s.poe_pct)}`, href: cheminAppareil(s.device.uid) })
    }
    for (const p of s.ports) {
      const e = etatPort(p)
      if (e.ton !== 'warn' && e.ton !== 'err') continue
      out.push({ ton: e.ton, texte: `${s.device.display_name} port ${p.port_idx} : ${e.libelle.toLowerCase()}${p.peer ? ` (${p.peer.display_name})` : ''}`, href: cheminAppareil(s.device.uid) })
    }
  }
  for (const b of d.wifi?.access_points ?? []) {
    appareil(b.device, 'Borne')
    for (const r of b.radios) {
      if (estPerime(r.data_age_s) || (r.level !== 'warning' && r.level !== 'critical')) continue
      out.push({
        ton: tonMesure(r.level),
        texte: `${b.device.display_name} ${r.band ? BANDE[r.band] ?? r.band : r.radio}${r.channel !== null ? ` (canal ${r.channel})` : ''} : ${LIBELLE_MESURE[r.level].toLowerCase()} — ${causeRadio(r, d.wifi?.thresholds)}`,
        href: cheminAppareil(b.device.uid),
      })
    }
  }
  for (const ap of d.paiement?.aps ?? []) {
    for (const t of ap.terminals) {
      const e = etatTerminal(t)
      if (e.ton === 'err' || e.ton === 'warn') out.push({ ton: e.ton, texte: `TPE ${t.name} : ${e.libelle.toLowerCase()} (sous ${ap.name})`, href: cheminAppareil(t.uid) })
    }
  }
  for (const t of d.paiement?.absent ?? []) {
    // `absent` = UniFi répond et ne voit pas le TPE ; une présence inconnue (null) n'est jamais comptée ici.
    if (t.present === false) out.push({ ton: 'warn', texte: `TPE ${t.name} : pas vu sur le Wi-Fi`, href: cheminAppareil(t.uid) })
  }
  for (const x of d.desaccords?.items ?? []) {
    out.push({ ton: 'warn', texte: `${x.name} : les sources ne sont pas d’accord`, ...(x.uid ? { href: cheminAppareil(x.uid) } : {}) })
  }
  return trierAttention(out)
}

/** Verdict d'en-tête : gris tant qu'on ne sait rien, jamais vert par défaut. */
/**
 * Verdict de tête. `figeDepuis` : la lecture a échoué (ou le collecteur est
 * muet) et l'écran montre une DERNIÈRE donnée — gris, jamais « Rien à
 * signaler » en vert sur une donnée qu'on ne relit plus.
 */
export function verdictAttention(points: PointAttention[], donnees: boolean, fige = false, figeDepuis: string | null = null): { ton: Ton; texte: string } {
  if (!donnees) return { ton: 'gris', texte: 'Pas de donnée de Sonde' }
  if (fige) {
    const h = figeDepuis ? new Date(figeDepuis).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris' }) : null
    return { ton: 'gris', texte: h ? `Dernière donnée de ${h}` : 'Supervision indisponible' }
  }
  const err = points.filter((p) => p.ton === 'err').length
  const warn = points.filter((p) => p.ton === 'warn').length
  if (err > 0) return { ton: 'err', texte: `${err} critique${err > 1 ? 's' : ''}${warn > 0 ? ` · ${warn} à surveiller` : ''}` }
  if (warn > 0) return { ton: 'warn', texte: `${warn} point${warn > 1 ? 's' : ''} à surveiller` }
  return { ton: 'ok', texte: 'Rien à signaler' }
}

/** Âge le plus ancien d'un ensemble de mesures (ce que vaut un chiffre agrégé) ; `null` si aucun. */
export function agePlusVieux(ages: readonly (number | null | undefined)[]): number | null {
  const a = ages.filter((x): x is number => typeof x === 'number' && Number.isFinite(x))
  return a.length ? Math.max(...a) : null
}

/**
 * Couleur d'une carte « borne → TPE » : rose si un TPE est à risque élevé,
 * ambre si l'un est à surveiller, vert SEULEMENT si tous sont à risque
 * faible ; sinon (risque inconnu) gris.
 */
export function tonBornePaiement(terminaux: readonly Pick<Terminal, 'present' | 'level'>[]): Ton {
  const tons = terminaux.map((t) => etatTerminal(t).ton)
  if (tons.includes('err')) return 'err'
  if (tons.includes('warn')) return 'warn'
  return tons.length > 0 && tons.every((t) => t === 'ok') ? 'ok' : 'gris'
}

/** Compte des états d'une liste d'appareils (pour les tuiles). */
export function compteEtats(etats: string[]): { enLigne: number; eteints: number; problemes: number; hs: number; gris: number; total: number } {
  const c = { enLigne: 0, eteints: 0, problemes: 0, hs: 0, gris: 0, total: etats.length }
  for (const e of etats) {
    if (e === 'ONLINE') c.enLigne++
    else if (e === 'OFF_EXPECTED' || e === 'MAINTENANCE') c.eteints++
    else if (e === 'OFFLINE') c.hs++
    else if (e === 'DEGRADED') c.problemes++
    else c.gris++
  }
  return c
}

/** Ton d'une famille d'appareils : rouge si l'un est hors ligne, ambre si dégradé, gris si tout est inconnu. */
export function tonCompte(c: ReturnType<typeof compteEtats>, alertes = 0): Ton {
  if (c.total === 0) return 'gris'
  if (c.hs > 0) return 'err'
  if (c.problemes > 0 || alertes > 0) return 'warn'
  if (c.enLigne === 0 && c.eteints === 0) return 'gris'
  return 'ok'
}
